// --- Dependencies -------------------------------------------------------------------------------
import { Navigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useStore from "@/hooks/useStore";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The public menu, rendered for its own owner. It opens in a new tab and sits outside the dashboard
 * layout on purpose — a sidebar around it would misrepresent what a customer actually sees.
 *
 * The page comes from the API as markup rather than a URL because a plain navigation cannot carry the
 * `Authorization` header, and this view has to work for stores with no subscription, which the public
 * `{slug}.menuket.net` page deliberately refuses to show.
 */
export default function PreviewMenuScreen() {
    const t = useTranslation();
    const token = useAuth((state) => state.token);
    const storeId = useStore((state) => state.storeId);

    const { data, loading, error } = useFetch<TyApiResponse<{ html: string }>>("/menu/preview");

    if (!token) return <Navigate to="/login" replace />;
    if (!storeId) return <Navigate to="/select-store" replace />;

    const html = data?.data?.html;

    return (
        <div className="preview-menu">
            {loading && <p className="preview-menu-note">{t.previewMenu.loading}</p>}
            {!loading && error && <p className="preview-menu-note preview-menu-note--error">{error}</p>}

            {/* Sandboxed into its own document so the menu's own styles and script behave as they do live. */}
            {!loading && !error && html && <iframe className="preview-menu-frame" title={t.previewMenu.title} srcDoc={html} />}
        </div>
    );
}
